import React from "react";
import { LucideCheckCircle, LucidePlayCircle } from "lucide-react";
import { LuClipboardList, LuDatabase, LuRefreshCw, LuTrendingUp } from "react-icons/lu";
import { FiCheckCircle, FiDatabase, FiPlayCircle, FiRefreshCw, FiTarget, FiTrendingUp } from "react-icons/fi";

export const performanceMarketingHeroData = {
  backgroundImage: "/images/serviceBanners/PERFORMANCEMARKETING&PAIDMEDIA.webp",
  whiteImage: "/images/serviceBanners/PERFORMANCEMARKETING&PAIDMEDIAW.webp",
  subTitle: "Performance Marketing & Paid Media Solutions",
  title: (
    <>
      High Performance <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">ROI Driven Growth </span> System
    </>
  ),
  description:
    "Performance marketing focuses on measurable growth through precise tracking, optimized ad campaigns, and data driven decisions that maximize return on ad spend while improving overall marketing efficiency and business outcomes.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("AI Start clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("AI Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Full Funnel Campaign Strategy",
      desc: "Optimized for speed and sub-second inference.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Paid Ads Performance Optimization",
      desc: "Cloud-agnostic deployments (AWS/GCP/Azure).",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Conversion Tracking Systems",
      desc: "Enterprise-grade model governance and privacy.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Scalable Media Buying",
      desc: "Clean, maintainable, and monitorable pipelines.",
      iconColor: "#34d399",
    },
  ],
};

export const performanceMarketingAndProcessData = {
  buildSubtitle: "PERFORMANCE MARKETING",
  buildTitle: "Performance Marketing Engineering Framework",
  buildCards: [
    {
      title: "Campaign Architecture Planning",
      desc: "Define paid media structure, funnel flow, and conversion strategy for scalable performance.",
      icon: FiTarget,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Agile Campaign Execution",
      desc: "Run iterative testing of ads, creatives, audiences, and bidding strategies for continuous improvement.",
      icon: FiRefreshCw,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Campaign Development System",
      desc: "Create structured ad campaigns across Google Ads, Meta Ads, and other paid platforms.",
      icon: FiPlayCircle,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Data Tracking & Attribution Setup",
      desc: "Implement accurate tracking systems for clicks, conversions, and ROI measurement.",
      icon: FiDatabase,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
    {
      title: "Performance Optimization System",
      desc: "Continuously improve CTR, CPC, ROAS, and conversion rates using real data insights.",
      icon: FiTrendingUp,
      colorClass: "text-[#ec4899]",
      bgClass: "bg-[#ec4899]/10",
      borderClass: "border-[#ec4899]/20 hover:border-[#ec4899]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(236,72,153,0.25)]",
    },
    {
      title: "Quality Assurance & Scaling",
      desc: "Ensure stable campaign performance and scale profitable ads across multiple channels.",
      icon: FiCheckCircle,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
  ],
  processSubtitle: "PERFORMANCE MARKETING ENGINEERING FRAMEWORK",
  processTitle: "How We Build Scalable Paid Media Systems",
  processSteps: [
    {
      step: "01",
      title: "Campaign Architecture Planning",
      description: "Define paid media structure, funnel flow, and conversion strategy for scalable performance.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Agile Campaign Execution",
      description: "Run iterative testing of ads, creatives, audiences, and bidding strategies for continuous improvement.",
      icon: LuRefreshCw,
    },
    {
      step: "03",
      title: "Campaign Development System",
      description: "Create structured ad campaigns across Google Ads, Meta Ads, and other paid platforms.",
      icon: LucidePlayCircle,
    },
    {
      step: "04",
      title: "Data Tracking & Attribution Setup",
      description: "Implement accurate tracking systems for clicks, conversions, and ROI measurement.",
      icon: LuDatabase,
    },
    {
      step: "05",
      title: "Performance Optimization System",
      description: "Continuously improve CTR, CPC, ROAS, and conversion rates using real data insights.",
      icon: LuTrendingUp,
    },
    {
      step: "06",
      title: "Quality Assurance & Scaling",
      description: "Ensure stable campaign performance and scale profitable ads across multiple channels.",
      icon: LucideCheckCircle,
    },
  ],
  benefitsSubtitle: "WHY INVEST IN PERFORMANCE MARKETING SYSTEMS?",
  benefitsTitle: "Why Businesses Choose Performance Marketing",
  benefits: [
    "Measurable ROI driven growth",
    "Scalable customer acquisition system",
    "Real time performance tracking",
    "Data backed decision making",
    "High conversion optimization",
    "Multi platform campaign control",
    "Sustainable revenue growth model",
  ],
  logoImage: "/images/serviceBanners/PerformanceMarketingBanner.webp",
  whiteLogoImage: "/images/serviceBanners/PerformanceMarketingW.webp",
};

export const performanceMarketingAboutData = [
  {
    title: "Performance Marketing Services for Measurable Business Growth",
    content: (
      <>
        Businesses today need marketing systems that deliver clear results instead of relying on assumptions and guesswork. Professional{" "}
        <b className="text-text-primary font-semibold">Performance Marketing Services</b> help organizations improve customer acquisition, increase revenue opportunities, and make
        smarter investment decisions through data-driven execution. Many companies invest heavily in advertising but struggle to understand which campaigns truly contribute to
        growth. At <b className="text-text-primary font-semibold">Tycho Technologies</b>, performance-focused strategies are built around customer behavior, market insights, and
        business objectives to create predictable and scalable growth opportunities.
      </>
    ),
  },
  {
    title: "Paid Media Services That Improve Campaign Efficiency",
    content: (
      <>
        Successful advertising requires more than launching campaigns across multiple platforms. Reliable <b className="text-text-primary font-semibold">Paid Media Services</b>{" "}
        help businesses reach the right audience while maintaining control over costs and performance. Organizations that invest in structured media planning often achieve stronger
        outcomes because campaigns are guided by audience intent and measurable goals. Tycho Technologies develops customized paid media frameworks that help businesses improve
        visibility, attract qualified prospects, and create stronger opportunities for conversion and revenue generation.
      </>
    ),
  },
  {
    title: "Google Ads Management Services for Better Customer Acquisition",
    content: (
      <>
        Reaching potential customers at the right moment can significantly improve marketing performance. Effective{" "}
        <b className="text-text-primary font-semibold">Google Ads Management Services</b> help businesses connect with users actively searching for products and services,
        increasing the likelihood of meaningful engagement. Companies that focus on targeted advertising often experience improved lead quality and stronger conversion
        opportunities. A strategic advertising approach allows organizations to maximize marketing efficiency while building a more sustainable customer acquisition process.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Performance Marketing Agency India",
    content: (
      <>
        The demand for expert <b className="text-text-primary font-semibold">ROI Driven Marketing Services</b> continues to increase as businesses seek greater accountability from
        their marketing investments. Companies partnering with a trusted <b className="text-text-primary font-semibold">Performance Marketing Agency India</b>
        often focus on improving profitability, increasing customer acquisition efficiency, and strengthening long-term growth. Businesses working with{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b> gain access to customized marketing systems designed around measurable business goals and performance
        insights. A data-focused marketing strategy helps organizations improve decision making, optimize campaign outcomes, and create sustainable revenue growth.
      </>
    ),
  },
];

export const performanceMarketingFaqs = [
  {
    q: "What is the difference between performance marketing and paid media advertising?",
    a: "Performance marketing focuses on measurable business outcomes such as leads, sales, and revenue, while paid media refers to the advertising channels used to achieve those outcomes. Together, they create a data-driven system for scalable business growth.",
  },
  {
    q: "Which paid media platforms deliver the best results for business growth?",
    a: "The most effective platforms depend on your audience and goals. Businesses commonly use Google Ads, Meta Ads, LinkedIn Ads, YouTube Ads, and programmatic advertising to reach high-intent customers and generate conversions.",
  },
  {
    q: "How does audience retargeting improve paid media campaign performance?",
    a: "Audience retargeting reconnects with users who previously visited your website, engaged with ads, or showed purchase intent. This helps increase conversion rates, improve return on ad spend, and recover potential lost opportunities.",
  },
  {
    q: "How does Tycho Technologies optimize paid media campaigns for better ROI?",
    a: "Tycho Technologies uses advanced audience targeting, conversion tracking, landing page optimization, attribution analysis, and continuous campaign testing to improve advertising efficiency and maximize return on investment.",
  },
  {
    q: "Why choose Tycho Technologies for performance marketing and paid media services?",
    a: "Tycho Technologies develops customized paid media systems that combine strategic planning, multi-platform campaign management, real-time analytics, and scalable optimization frameworks to help businesses achieve sustainable growth and measurable results.",
  },
];
